import { useState } from 'react'
import { motion } from 'framer-motion'
import { 
  Copy,
  CheckCircle,
  Calendar,
  MapPin,
  Briefcase,
  Users,
  Eye,
  Trash2,
  Share2
} from 'lucide-react'
import { useStore } from '../../store/useStore'
import GlassCard from '../../components/ui/GlassCard'
import Button from '../../components/ui/Button'
import AdminLayout from '../../components/admin/AdminLayout'

const JobManagement = () => {
  const { jobPostings, updateJobPosting, deleteJobPosting } = useStore()
  const [copiedId, setCopiedId] = useState(null)

  // Offres d'emploi pré-établies
  const predefinedJobs = [
    {
      id: 'pab',
      title: 'Préposé au Bénéficiaire (PAB)',
      position: 'Préposé au Bénéficiaire (PAB)',
      location: 'Laval, QC',
      description: 'Nous recherchons un Préposé au Bénéficiaire pour rejoindre notre équipe. Responsable d\'assister les résidents dans leurs activités quotidiennes.',
      requirements: 'Diplôme PAB, expérience avec personnes en déficience intellectuelle, certificat RCR à jour',
      benefits: 'Assurances, horaires flexibles, environnement stimulant, formation continue',
      category: 'healthcare'
    },
    {
      id: 'aide-soignant',
      title: 'Aide-Soignant',
      position: 'Aide-Soignant',
      location: 'Laval, QC',
      description: 'Poste d\'Aide-Soignant pour fournir des soins de qualité aux résidents.',
      requirements: 'Diplôme en soins infirmiers auxiliaires, 1-2 ans d\'expérience, certificat RCR',
      benefits: 'Régime complet d\'assurances, congés personnels, formation continue',
      category: 'healthcare'
    },
    {
      id: 'agent-intervention',
      title: 'Agent d\'Intervention',
      position: 'Agent d\'Intervention',
      location: 'Laval, QC',
      description: 'Agent d\'intervention pour développer et animer des activités avec les résidents.',
      requirements: 'DEC ou équivalent, expérience en intervention sociale, créativité',
      benefits: 'Horaires réguliers, opportunités de croissance, environnement dynamique',
      category: 'social'
    },
    {
      id: 'assistant-infirmier',
      title: 'Assistant Infirmier',
      position: 'Assistant Infirmier',
      location: 'Laval, QC',
      description: 'Assistant infirmier pour soutenir l\'équipe soignante dans les soins aux résidents.',
      requirements: 'DEC en soins infirmiers, expérience en milieu communautaire, certificat RCR',
      benefits: 'Salarie compétitif, assurances, horaires stables',
      category: 'healthcare'
    },
    {
      id: 'responsable-operation',
      title: 'Responsable d\'Opération',
      position: 'Responsable d\'Opération',
      location: 'Laval, QC',
      description: 'Responsable d\'opération pour superviser les activités quotidiennes et coordonner l\'équipe.',
      requirements: 'Baccalauréat en gestion, expérience en gestion d\'équipe, leadership',
      benefits: 'Régime complet, horaires réguliers, développement professionnel',
      category: 'management'
    }
  ]

  const handlePublish = (job) => {
    const jobData = {
      ...job,
      status: 'active',
      publishDate: new Date().toISOString(),
      closingDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString() // 30 jours
    }
    
    // Vérifier si l'offre existe déjà
    const existingJob = jobPostings.find(j => j.id === job.id)
    if (existingJob) {
      updateJobPosting(job.id, { ...existingJob, ...jobData })
    } else {
      // Ajouter via le store directement
      window.location.reload() // Refresh pour voir les changements
    }
  }

  const handleUnpublish = (jobId) => {
    const job = jobPostings.find(j => j.id === jobId)
    if (job) {
      updateJobPosting(jobId, { ...job, status: 'inactive' })
    }
  }

  const copyLink = (jobId) => {
    const url = `${window.location.origin}/jobs/${jobId}`
    navigator.clipboard.writeText(url)
    setCopiedId(jobId)
    setTimeout(() => setCopiedId(null), 2000)
  }

  const getShareableLink = (jobId) => {
    return `${window.location.origin}/jobs/${jobId}`
  }

  const isJobPublished = (jobId) => {
    return jobPostings.some(j => j.id === jobId && j.status === 'active')
  }

  return (
    <AdminLayout>
      <div className="py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900">Gestion des Offres d'Emploi</h1>
            <p className="text-gray-600">Publiez ou dépubliez les offres d'emploi pré-établies</p>
          </div>

          {/* Offres Pré-établies */}
          <div className="space-y-6">
            {predefinedJobs.map((job) => {
              const isPublished = isJobPublished(job.id)
              
              return (
                <motion.div
                  key={job.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <GlassCard className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <h3 className="text-2xl font-bold text-gray-900">{job.title}</h3>
                          {isPublished && (
                            <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium">
                              Publiée
                            </span>
                          )}
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4 text-sm text-gray-600">
                          <div className="flex items-center space-x-2">
                            <Briefcase className="w-4 h-4" />
                            <span>{job.position}</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <MapPin className="w-4 h-4" />
                            <span>{job.location}</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-3 mb-4">
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-1">Description</h4>
                        <p className="text-gray-600 text-sm">{job.description}</p>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-1">Exigences</h4>
                        <p className="text-gray-600 text-sm">{job.requirements}</p>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-1">Avantages</h4>
                        <p className="text-gray-600 text-sm">{job.benefits}</p>
                      </div>
                    </div>

                    <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                      {isPublished && (
                        <div className="flex items-center space-x-2">
                          <Share2 className="w-4 h-4 text-gray-500" />
                          <input
                            type="text"
                            value={getShareableLink(job.id)}
                            readOnly
                            className="px-3 py-1 border border-gray-300 rounded text-sm bg-gray-50 flex-1 max-w-md"
                          />
                          <button
                            onClick={() => copyLink(job.id)}
                            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                          >
                            {copiedId === job.id ? (
                              <>
                                <CheckCircle className="w-4 h-4" />
                                <span>Copié!</span>
                              </>
                            ) : (
                              <>
                                <Copy className="w-4 h-4" />
                                <span>Copier</span>
                              </>
                            )}
                          </button>
                        </div>
                      )}

                      <div className="flex items-center space-x-2 ml-auto">
                        <Button
                          onClick={() => isPublished ? handleUnpublish(job.id) : handlePublish(job)}
                          className={isPublished ? 'bg-red-600 hover:bg-red-700 text-white' : 'bg-green-600 hover:bg-green-700 text-white'}
                        >
                          {isPublished ? 'Dépublier' : 'Publier'}
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => window.open(`/jobs/${job.id}`, '_blank')}
                        >
                          <Eye className="w-4 h-4 mr-2" />
                          Aperçu
                        </Button>
                      </div>
                    </div>
                  </GlassCard>
                </motion.div>
              )
            })}
          </div>
        </div>
      </div>
    </AdminLayout>
  )
}

export default JobManagement